import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import { BackendIntegrationService } from '../lib/backend-integration.service';
import { Transition } from '../lib/transition.model';
import { WorkflowEditorStore } from '../lib/workflow-editor.store';
import {
  WorkflowTransitionFormComponent,
  WorkflowTransitionFormOptions,
} from './transition-form.component';
import { WorkflowTransitionFormResult } from './transition-form.model';
import { TransitionAction } from '../lib/transition-action.model';
import { TransitionGuard } from '../lib/transition-guard.model';
import { WorkflowInitialTransitionFormComponent } from './initial-transition-form.component';
import { WorkflowInitialTransitionFormOptions } from './initial-transition-form.model';

@Component({
    selector: 'eas-workflow-transitions',
    templateUrl: './transition-list.component.html',
    styleUrls: ['./transition-list.component.css'],
    standalone: false
})
export class WorkflowTransitionListComponent implements OnInit {

  private itemFormDialogRef?:
    MatDialogRef<WorkflowTransitionFormComponent, WorkflowTransitionFormResult> |
    MatDialogRef<WorkflowInitialTransitionFormComponent, WorkflowTransitionFormResult>;

  public items: Transition[] = [];

  public get availableActions(): TransitionAction[] {
    return this.integration.availableActions;
  }

  public get availableGuards(): TransitionGuard[] {
    return this.integration.availableGuards;
  }

  constructor(private cdRef: ChangeDetectorRef,
              private store: WorkflowEditorStore,
              private dialog: MatDialog,
              private integration: BackendIntegrationService) { }

  ngOnInit(): void {
    this.store.workflow$.subscribe(workflow => {
      const items = [];
      if (workflow) {
        if (workflow.initialTransition) { items.push(workflow.initialTransition); }
        items.push(...workflow.transitions);
      }
      this.items = items;
      this.cdRef.detectChanges();
    });
  }

  //
  // Template helpers
  //

  public get hasTransitions(): boolean {
    return this.items.length > 0;
  }

  //
  // Actions
  //

  public onAddTransition(): void {
    this.presentTransitionEditorDialog(undefined, (result) => {
      this.store.addTransition(result);
    });
  }

  public onDeleteTransition(transitionID: string): void {
    this.store.deleteTransition(transitionID);
  }

  public onEditTransition(transitionID: string): void {
    // FIXME: Refactor into Store/Workflow
    // this.store.findTransitionByID(transitionID) => InitalTransition | Transition
    let transition = this.store.transitions.find(t => t.id === transitionID);
    if (!transition && this.store.workflow?.initialTransition?.id === transitionID) {
      transition = this.store.workflow.initialTransition;
    }

    // FIXME: Is this correct error handling?
    //        How to make it so it's pickuped by Sentry
    if (!transition) {
      console.warn(`Unable to find transition with ID '${transitionID}'`);
      return;
    }

    if (transition.isInitialTransition) {
      this.presentInitialTransitionEditorDialog(transition, (result) => {
        this.store.updateTransition(transitionID, result);
      });
    } else {
      this.presentTransitionEditorDialog(transition, (result) => {
        this.store.updateTransition(transitionID, result);
      });
    }
  }

  public onFocusTransition(transitionID: string|undefined): void {
    this.store.focusTransition(transitionID);
  }

  public onPatchTransition(update: Partial<Transition>): void {
    if (!update.id) { return; }
    this.store.updateTransition(update.id, update);
  }

  private presentTransitionEditorDialog(transition: Transition|undefined,
                                        afterClosed?: (result: WorkflowTransitionFormResult) => void): void {
    if (this.itemFormDialogRef) { return; }

    const options: MatDialogConfig<WorkflowTransitionFormOptions> = {
      minWidth: '50vw',
      id: 'workflow-editor-form-dialog',
      data: {
        'transition': transition,
        roles: this.store.createRolesSelectData(),
        states: this.store.workflow?.states ?? []
      }
    };

    this.itemFormDialogRef =
      this.dialog.open<WorkflowTransitionFormComponent,
                       WorkflowTransitionFormOptions,
                       WorkflowTransitionFormResult>(WorkflowTransitionFormComponent, options);
    this.itemFormDialogRef.afterClosed().subscribe(result => {
      this.itemFormDialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }

  private presentInitialTransitionEditorDialog(transition: Transition|undefined,
                                               afterClosed?: (result: WorkflowTransitionFormResult) => void): void {
    if (this.itemFormDialogRef) { return; }

    const options: MatDialogConfig<WorkflowInitialTransitionFormOptions> = {
      minWidth: '50vw',
      id: 'workflow-editor-form-dialog',
      data: {
        'transition': transition,
        roles: this.store.createRolesSelectData(),
        states: this.store.workflow?.states ?? []
      }
    };

    this.itemFormDialogRef =
      this.dialog.open<WorkflowInitialTransitionFormComponent,
                       WorkflowInitialTransitionFormOptions,
                       WorkflowTransitionFormResult>(WorkflowInitialTransitionFormComponent, options);
    this.itemFormDialogRef.afterClosed().subscribe(result => {
      this.itemFormDialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }
}
